import {
  Button,
  Modal,
  Form,
  Input,
  Flex,
  Typography,
  message,
  Select,
  DatePicker,
} from "antd";
import React, { forwardRef, useState, useImperativeHandle } from "react";
import { EditOutlined, BankOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";
import dayjs from "dayjs";

const AddModal = forwardRef((props, ref) => {
  const { onSuccess } = props;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  useImperativeHandle(ref, () => ({
    setModelAdd: async () => {
      form.resetFields();
      setVisible(true);
      await Promise.all([stateSelect.load(), vendorSelect.load()]);
    },
  }));

  function useLazySelect(apiUrl, cacheKey, mapper, enableCache = true) {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async (params = {}) => {
      const hasParams = params && Object.keys(params).length > 0;
      if (!hasParams && options.length && enableCache) return;

      setLoading(true);
      try {
        let res;
        if (hasParams) {
          res = await http.get(apiUrl, { params });
        } else {
          res = await http.get(apiUrl);
        }

        const raw = res?.data || [];
        setOptions(mapper(raw));
      } finally {
        setLoading(false);
      }
    };

    return { options, loading, load };
  }
  // const stateSelect = useLazySelect(api.getStateDictList, "state", (data) =>
  //   data.map((s) => ({ label: s, value: s })),
  // );
    const stateSelect = useLazySelect(api.getStateDictList, "state", (data) =>
    Object.entries(data || {}).map(([key, label]) => ({
      label,   
      value: key, 
    })),
  );

  const vendorSelect = useLazySelect(api.vendorSearch, "vendor", (data) =>
    data.map((v) => ({
      label: v.vendorName,
      value: String(v.vendorId),
    })),
  );

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        startDate: values.startDate
          ? dayjs(values.startDate).startOf("day").valueOf()
          : null,
      };

      const res = await http.post(api.getCompanyAdd, payload);

      if (res?.code === "1") {
        message.success("Company Created Successfully");
        setVisible(false);
        form.resetFields();
        onSuccess && onSuccess();
      } else {
        message.error(res?.msg);
      }
    } catch (err) {
      console.error(err);
      message.error("Request failed, please try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Flex justify="flex-start" align="flex-start" className="model_title">
          <EditOutlined className="model_title_mr10" />
          <Typography.Title level={4} className="mg">
            CREATE COMPANY
          </Typography.Title>
        </Flex>
      }
      width="60%"
      open={visible}
      onCancel={() => {
        setVisible(false);
        form.resetFields();
      }}
      maskClosable={false}
      footer={
        <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
          <Button
            key="company"
            onClick={() => {
              setVisible(false);
              form.resetFields();
            }}
          >
            Cancel
          </Button>
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            Submit
          </Button>
        </div>
      }
    >
      <Flex justify="space-between" align="flex-start">
        <div className="model_top" style={{ width: "100%" }}>
          <div className="model_content">
            <Form
              form={form}
              name="createBankForm"
              layout="horizontal"
              labelCol={{ style: { width: "200px" } }}
              wrapperCol={{ style: { flex: 1 } }}
              onFinish={onFinish}
            >
              <Flex align="flex-start" justify="space-between" gap={32}>
                <div style={{ flex: 1 }}>
                  <div className="modal-section-title">
                    <BankOutlined /> Company Detail
                  </div>

                  <Form.Item
                    name="companyName"
                    label="Company Name"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Company Name" />
                  </Form.Item>

                  <Form.Item
                    name="companyCode"
                    label="Company Code"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Company Code" />
                  </Form.Item>

                  <Form.Item
                    name="taxId"
                    label="Tax Id"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Tax Id" />
                  </Form.Item>

                  <Form.Item
                    name="street"
                    label="Street"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Street" />
                  </Form.Item>

                  <Form.Item name="city" label="City">
                    <Input placeholder="City" />
                  </Form.Item>

                  <Form.Item
                    name="state"
                    label="State"
                    rules={[{ required: true }]}
                  >
                    <Select
                      placeholder="Select State"
                      loading={stateSelect.loading}
                      onDropdownVisibleChange={(open) =>
                        open && stateSelect.load()
                      }
                      options={stateSelect.options}
                    />
                  </Form.Item>

                  <Form.Item
                    name="zip"
                    label="Zip"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Zip" />
                  </Form.Item>

                  <Form.Item
                    name="vendid"
                    label="Vendor"
                    rules={[{ required: true }]}
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select Vendor"
                      loading={vendorSelect.loading}
                      options={vendorSelect.options}
                      filterOption={false}
                      onSearch={(value) =>
                        vendorSelect.load({ content: value })
                      }
                      onDropdownVisibleChange={(open) => {
                        if (open && vendorSelect.options.length === 0) {
                          vendorSelect.load();
                        }
                      }}
                      notFoundContent={
                        vendorSelect.loading ? "Loading..." : "No Data"
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    name="mailingStreet"
                    label="Mailing Street"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Mailing Street" />
                  </Form.Item>

                  <Form.Item
                    name="mailingCity"
                    label="Mailing City"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Mailing City" />
                  </Form.Item>

                  <Form.Item
                    name="mailingState"
                    label="Mailing State"
                    rules={[{ required: true }]}
                  >
                    <Select
                      placeholder="Select MailingState"
                      loading={stateSelect.loading}
                      onDropdownVisibleChange={(open) =>
                        open && stateSelect.load()
                      }
                      options={stateSelect.options}
                    />
                  </Form.Item>

                  <Form.Item
                    name="mailingZip"
                    label="Mailing Zip"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Mailing Zip" />
                  </Form.Item>

                  <Form.Item
                    name="phone"
                    label="Phone"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Phone" />
                  </Form.Item>

                  <Form.Item name="eMail" label="Email">
                    <Input placeholder="Email" />
                  </Form.Item>

                  <Form.Item name="fax" label="Fax">
                    <Input placeholder="Fax" />
                  </Form.Item>
                  <Form.Item
                    name="startDate"
                    label="Start Date"
                    rules={[
                      { required: true, message: "Please select start date" },
                    ]}
                  >
                    <DatePicker
                      format="MM/DD/YYYY"
                      style={{ width: "100%" }}
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item name="remark" label="Remark">
                    <Input placeholder="Remark" />
                  </Form.Item>
                </div>
              </Flex>
            </Form>
          </div>
        </div>
      </Flex>
    </Modal>
  );
});

export default AddModal;
